'use client';

import React from 'react';
import Link from 'next/link';
import { BsInstagram, BsFacebook } from 'react-icons/bs';
import PrimaryButton from '@/components/shared/PrimaryButton';
import { siteConfig } from '@/lib/seo-config';
import { trackEvent } from '@/lib/amplitude';

/**
 * Figma mobile 303:3146. Mobile below the hairline: a pink band, monochrome icon-badge
 * socials, uppercase dot-separated legal links, uppercase copyright line.
 *
 * Desktop is a dark multi-column footer per the reference design (Explore / Follow us /
 * Need some help?, then a legal+copyright bar). The reference shows
 * placeholder content from a different site (book-genre links, a region switcher) — those
 * are swapped for this site's real pages/links rather than copied verbatim; Ink With Magic
 * has one product line and no region switcher to wire up.
 *
 * Links are the live site's real destinations. The reference shows a third social icon
 * (TikTok), but no TikTok account exists anywhere in the codebase, so it's omitted rather
 * than dead-linked — add it to SOCIALS once a real handle exists.
 */
const SOCIALS = [
  {
    href: siteConfig.social.instagramUrl,
    Icon: BsInstagram,
    label: 'Visit our Instagram page',
    id: 'link-instagram',
  },
  {
    href: siteConfig.social.facebookUrl,
    Icon: BsFacebook,
    label: 'Visit our Facebook page',
    id: 'link-facebook',
  },
];

/** Same links as the site's hamburger nav menu (Header.tsx), minus its legal links —
 * those already have their own section (LEGAL) in this footer. */
const EXPLORE = [
  { href: '/comix', label: 'Create a comic' },
  { href: '/comix/select', label: 'My characters' },
  { href: '/comix/stories', label: 'My comics' },
  // About/Contact added: /comix (the money page) had no crawlable path to either — its
  // only outbound links were the 3 above (2 of which are robots.txt-disallowed session
  // flows) and the legal pages, so /about (the site's only real E-E-A-T page) was
  // orphaned from the page everyone actually lands on.
  { href: '/about', label: 'About us' },
  { href: '/contact', label: 'Contact us' },
];

const LEGAL = [
  { href: '/terms-services', label: 'Terms of Use' },
  { href: '/privacy-policy', label: 'Privacy Policy' },
  { href: '/return-policy', label: 'Return Policy' },
];

/** Mobile-only: Privacy first, per the reference (desktop keeps LEGAL's order above). */
const MOBILE_LEGAL = [
  { href: '/privacy-policy', label: 'Privacy Policy' },
  { href: '/terms-services', label: 'Terms of Use' },
  { href: '/return-policy', label: 'Return Policy' },
];

export default function HomeFooter() {
  return (
    // bg is mobile-only (pink band, per the reference) — dt:bg-transparent keeps desktop
    // exactly as before (plain white, showing through from the page background).
    <footer className="w-full rounded-tl-[28px] bg-[#F1EAEA] pb-[60px] dt:rounded-none dt:bg-transparent dt:pb-0">
      {/* Mobile: "Need help?" band + stacked social/legal/copyright, pink-band styling
          per the mobile reference. Hidden from dt up — desktop has its own section below. */}
      <div className="hp-container pt-[var(--hp-section-y)] dt:hidden">
        <div className="flex flex-col items-center text-center">
          <h2
            className="font-bold tracking-[-0.454px] text-[var(--hp-ink)]"
            style={{ fontSize: 'var(--hp-h2)', lineHeight: 1.35 }}
          >
            Need help?
          </h2>
          {/* Forces the break after "happiness." — natural wrapping at this width put
              "Direct," on the first line instead. */}
          <p
            className="mt-3 max-w-[420px] text-[var(--hp-body)]"
            style={{ fontSize: 'var(--hp-lede)', lineHeight: 1.5 }}
          >
            We&apos;re committed to your happiness.
            <br />
            Direct, human support.
          </p>
          <PrimaryButton
            id="link-contact-us"
            href={`mailto:${siteConfig.emails.support}`}
            onClick={() => trackEvent('Home Footer - Contact Us', { breakpoint: 'mobile' })}
            size="small"
            className="mt-6"
          >
            Contact Us
          </PrimaryButton>
        </div>

        <hr className="mt-10 border-0 border-t border-[var(--hp-hairline)]" />

        <div className="flex flex-col items-center gap-8 py-10">
          <ul className="flex items-center gap-3">
            {SOCIALS.map(({ id, href, label, Icon }) => (
              <li key={id}>
                <a
                  id={id}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  onClick={() => trackEvent('Home Footer - Social Clicked', { platform: id.replace('link-', '') })}
                  className="flex size-11 items-center justify-center rounded-xl bg-white text-black shadow-sm transition-opacity duration-200 hover:opacity-70"
                >
                  <Icon size={18} aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>

          <ul
            className="flex flex-wrap items-center justify-center gap-x-2 gap-y-2 uppercase tracking-wide"
            style={{ fontSize: 'var(--hp-meta)' }}
          >
            {MOBILE_LEGAL.map((item, index) => (
              <React.Fragment key={item.href}>
                {index > 0 && (
                  <li aria-hidden="true" className="text-[var(--hp-body)] opacity-40">
                    ·
                  </li>
                )}
                <li>
                  <Link
                    href={item.href}
                    onClick={() => trackEvent('Home Footer - Legal Link Clicked', { label: item.label, href: item.href })}
                    className="text-[var(--hp-body)] underline underline-offset-2 opacity-70 transition-opacity duration-200 hover:opacity-100"
                  >
                    {item.label}
                  </Link>
                </li>
              </React.Fragment>
            ))}
          </ul>

          <p className="uppercase text-[var(--hp-body)] opacity-60" style={{ fontSize: 'var(--hp-meta)' }}>
            © Copyright 2026 {siteConfig.name}
          </p>
        </div>
      </div>

      {/* Desktop: dark multi-column footer per the reference — Explore / Follow us / Need
          some help?, then a legal + copyright bar. */}
      <div className="hp-footer-gradient hidden dt:block dt:pt-16 dt:pb-[88px]">
        <div className="mx-auto w-full max-w-[1040px] px-[var(--hp-gutter)]">
          {/* Explore + Follow us are grouped close together on the left (own nested flex);
              justify-between then pushes Need help? out to the right edge. flex-wrap +
              a fluid gap (clamp, not a fixed gap-16) keep this from overflowing between
              800-1040px, where the row has less room than at the full 1040px width. */}
          <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-8">
            <div className="flex items-start gap-[clamp(24px,6vw,64px)]">
              <div>
                <h3 className="font-semibold text-[var(--hp-ink)]" style={{ fontSize: 'var(--hp-meta)' }}>
                  Explore
                </h3>
                <ul className="mt-4 flex flex-col gap-3">
                  {EXPLORE.map((item) => (
                    <li key={item.href}>
                      <Link
                        href={item.href}
                        onClick={() => trackEvent('Home Footer - Explore Link Clicked', { label: item.label, href: item.href })}
                        className="text-[var(--hp-body)] opacity-70 transition-opacity duration-200 hover:opacity-100"
                        style={{ fontSize: 'var(--hp-meta)' }}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-[var(--hp-ink)]" style={{ fontSize: 'var(--hp-meta)' }}>
                  Follow us
                </h3>
                <ul className="mt-4 flex items-center gap-3">
                  {SOCIALS.map(({ id, href, label, Icon }) => (
                    <li key={id}>
                      <a
                        id={`${id}-desktop`}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={label}
                        onClick={() => trackEvent('Home Footer - Social Clicked', { platform: id.replace('link-', '') })}
                        className="flex size-11 items-center justify-center rounded-full bg-white text-black shadow-sm transition-opacity duration-200 hover:opacity-70"
                      >
                        <Icon size={18} aria-hidden="true" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div>
              {/* Same copy as the mobile "Need help?" band above, not the reference's
                  wording — kept consistent across both breakpoints. */}
              <h3 className="font-semibold text-[var(--hp-ink)]" style={{ fontSize: 'var(--hp-meta)' }}>
                Need help?
              </h3>
              <p className="mt-2 max-w-[280px] text-[var(--hp-body)] opacity-70" style={{ fontSize: 'var(--hp-meta)' }}>
                We&apos;re committed to your happiness. Direct, human support.
              </p>
              <PrimaryButton
                id="link-contact-us"
                href={`mailto:${siteConfig.emails.support}`}
                onClick={() => trackEvent('Home Footer - Contact Us', { breakpoint: 'desktop' })}
                size="small"
                className="mt-4"
              >
                Contact Us
              </PrimaryButton>
            </div>
          </div>

          <hr className="mt-12 border-0 border-t border-[var(--hp-hairline)]" />

          <div className="flex items-center justify-between py-6">
            <ul className="flex items-center gap-6">
              {LEGAL.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    onClick={() => trackEvent('Home Footer - Legal Link Clicked', { label: item.label, href: item.href })}
                    className="text-[var(--hp-body)] opacity-70 transition-opacity duration-200 hover:opacity-100"
                    style={{ fontSize: '14px' }}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="text-[var(--hp-body)] opacity-60" style={{ fontSize: '14px' }}>
              © 2026 {siteConfig.name}. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
